/**
 * Hook to check if approval workflow is configured/enabled for the storefront
 * enable_approval_workflow + ≥1 approval configuration + modify_products
 */
import { useMemo } from "react";
import useSWR from "swr";
import { endpoints } from "../../../../../endpoints";
import type { WithPagination } from "../../../../../types/types";
import type { ApprovalConfiguration } from "../../../../../types/types.Workflow";
import { useStoreState } from "../../../../../util/util";
import { useAuthContext } from "../../../../../components/Auth";

export function useApprovalWorkflowEnabled() {
  const {
    storefront_id,
    storefront_metadata: { enable_approval_workflow },
  } = useStoreState();
  const { hasPermission, user } = useAuthContext();

  const params = useMemo(
    () => ({
      params: { limit: 1, offset: 0 },
    }),
    []
  );

  const { data, error } = useSWR<
    WithPagination<{ data: ApprovalConfiguration[] }>
  >(
    // Only fetch once logged in: a pre-login 401 would otherwise delay the
    // request until SWR's next 5s error retry. user.id in the key refetches on login.
    storefront_id && enable_approval_workflow && user?.id
      ? [
          endpoints.v2_storefronts_id_integrations_approval_configurations(
            storefront_id
          ),
          params,
          user.id,
        ]
      : null
  );

  const hasConfigurations = (data?.data?.length ?? 0) > 0;
  const canModifyProducts = hasPermission("modify_products");

  const isEnabled =
    Boolean(enable_approval_workflow) && hasConfigurations && canModifyProducts;

  return {
    isEnabled,
    hasConfigurations,
    isLoading:
      Boolean(storefront_id && enable_approval_workflow && user?.id) &&
      !data &&
      !error,
  };
}
